import { useEffect } from 'react';
import { Button } from '@/core/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/core/components/ui/sheet';
import { TranslationForm } from '../molecules/TranslationForm';
import { TranslationKey, ApplicationType, Language, CreateTranslationRequest, UpdateTranslationRequest } from '../../types/translation-types';
import { useTranslationStore } from '../../stores/translation-store';
import { toast } from 'sonner';

interface TranslationBladeProps {
  isOpen: boolean;
  onClose: () => void;
  translation: TranslationKey | null;
  languages: Language[];
  applicationType: ApplicationType | null;
}

export function TranslationBlade({
  isOpen,
  onClose,
  translation,
  languages,
  applicationType,
}: TranslationBladeProps) {
  const {
    isSubmitting,
    submitError,
    createTranslation,
    updateTranslation,
    clearSubmitError,
  } = useTranslationStore();

  const isEditMode = translation !== null;
  const title = isEditMode ? 'Edit Translation' : 'Add Translation';

  // Clear errors when blade opens/closes
  useEffect(() => {
    if (isOpen) {
      clearSubmitError();
    }
  }, [isOpen, clearSubmitError]);

  const handleSubmit = async (formData: CreateTranslationRequest | UpdateTranslationRequest) => {
    try {
      if (isEditMode && translation) {
        await updateTranslation(translation.translationKeyId, formData as UpdateTranslationRequest);
        toast.success('Translation updated successfully');
      } else {
        if (!applicationType) {
          toast.error('Please select an application type first');
          return;
        }
        await createTranslation({
          ...(formData as CreateTranslationRequest),
          applicationType,
        });
        toast.success('Translation created successfully');
      }
      onClose();
    } catch {
      toast.error(isEditMode ? 'Failed to update translation' : 'Failed to create translation');
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-[500px] sm:w-[600px] sm:max-w-[600px] flex flex-col">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-6">
          <TranslationForm
            translation={translation}
            languages={languages}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            error={submitError}
            isEditMode={isEditMode}
            applicationType={applicationType}
          />
        </div>

        <SheetFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" form="translation-form" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
